import { createSelector } from '@reduxjs/toolkit';

/**
 * Whether a scenario genuinely spans more than one day — inferred from the solve
 * data, NOT from the UI toggle.
 *
 * Background: the `multipleDays` flag on the solve request used to be sent
 * straight from the global `timePeriodSpansDays` switch. That switch isn't reset
 * when you load a different scenario, so it drifts:
 *   - stale ON on a single-day problem  -> sent `multipleDays: true`, which the
 *     backend reads as "uses the multi-day premium feature" and would 403 a free
 *     user for (the false-positive that gated ENTITLEMENTS_ENFORCE);
 *   - stale OFF on a genuine multi-day example -> sent `false`, and the solver
 *     then ignores the day fields entirely (a silent wrong answer).
 *
 * The authoritative signal is the data. Every day-bearing input is a canonical
 * `*DayInt` column (`minPickupDayInt`, `maxArrivalDayInt`, `minStartDayInt`, …),
 * and day 0 is the single-day baseline, so a scenario spans multiple days iff
 * some `*DayInt` field carries a value > 0. Toggling the switch only changes
 * column visibility, never these values (see inputReducer `updateTimeSettings`),
 * so this is independent of the switch and of column visibility.
 */

const DAY_FIELD = /DayInt$/;

function rowSpansDays(row) {
  if (!row || typeof row !== 'object') return false;
  return Object.keys(row).some((key) => {
    if (!DAY_FIELD.test(key)) return false;
    const n = Number(row[key]);
    return Number.isFinite(n) && n > 0;
  });
}

/**
 * @param {object} problemData - the canonical ProblemData sent to /problem
 *   (locations / trips / vehicles carry the `*DayInt` fields; travelCosts don't,
 *   so they're skipped — that also avoids scanning the N×N matrix).
 * @returns {boolean} true if any location/trip/vehicle references a day > 0.
 */
export function scenarioSpansMultipleDays(problemData) {
  if (!problemData || typeof problemData !== 'object') return false;
  const rows = [
    ...(Array.isArray(problemData.locations) ? problemData.locations : []),
    ...(Array.isArray(problemData.trips) ? problemData.trips : []),
    ...(Array.isArray(problemData.vehicles) ? problemData.vehicles : []),
  ];
  return rows.some(rowSpansDays);
}

/**
 * Selector form of the above, reading the live input tables rather than a built
 * ProblemData — so the UI can tell BEFORE a solve whether this scenario will be
 * sent as multi-day, and warn a user whose plan doesn't include it instead of
 * letting the backend answer with an unexplained 403.
 *
 * Memoized on the three tables: it feeds a component that renders on every
 * store change, and re-scanning every row per dispatch is exactly the churn
 * that caused the earlier memory incident.
 */
export const selectScenarioSpansMultipleDays = createSelector(
  [
    (state) => state?.inputData?.present?.data?.locations,
    (state) => state?.inputData?.present?.data?.trips,
    (state) => state?.inputData?.present?.data?.vehicles,
  ],
  (locations, trips, vehicles) =>
    scenarioSpansMultipleDays({ locations, trips, vehicles })
);

/**
 * Whether the Optimize button should stop a multi-day solve with the
 * "Professional-plan feature" warning.
 *
 * Not for examples. A solve loaded from the gallery carries its `exampleSlug`
 * on the request, and the backend answers that with the `example` tier — which
 * extends `professional` and allows every feature, precisely because the
 * curated examples use them (six published examples span multiple days). So the
 * backend will accept the solve; warning first would tell the visitor to
 * upgrade for something they are about to be given. Daniel hit exactly that on
 * a gallery example and asked for the warning to stay, but not there.
 *
 * Keyed on the slug rather than `activeMode === 'example'` because the slug is
 * what the solve thunk actually sends (`generalSettings.scenario.exampleSlug`),
 * so the warning is skipped in exactly the cases the allowance applies.
 *
 * @param {object} args
 * @param {boolean} args.spansMultipleDays - From selectScenarioSpansMultipleDays.
 * @param {boolean} args.multiDayAllowed - From useFeature(FEATURE_MULTI_DAY).
 * @param {string|null|undefined} args.exampleSlug - From selectActiveExampleSlug.
 * @returns {boolean} True when the warning should block the run.
 */
export function shouldWarnMultiDay({ spansMultipleDays, multiDayAllowed, exampleSlug }) {
  if (!spansMultipleDays) return false;
  if (multiDayAllowed) return false;
  if (typeof exampleSlug === 'string' && exampleSlug.trim() !== '') return false;
  return true;
}
